'use client'

import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import type { ActionHandlerState } from '@/features/actions/logic/types'
import { ActionRow } from '@/features/actions/components/action-row/ActionRow'
import { DialogPanel } from '@/features/dialogs/common/components/DialogPanel'
import { MultiPanelDialog } from '@/features/dialogs/common/components/MultiPanelDialog'
import { TransactionOverviewDetailsItem } from '@/features/dialogs/common/components/transaction-overview/TransactionOverviewDetailsItem'
import { assets } from '@/ui/assets'
import { DialogTitle } from '@/ui/atoms/dialog/Dialog'
import { Slider } from '@/ui/atoms/slider/Slider'
import { TokenIcon } from '@/ui/atoms/token-icon/TokenIcon'
import { TransactionErrorPanel } from '@/ui/molecules/transaction-error-panel/TransactionErrorPanel'
import { TimeoutErrorDisplay } from '@/features/actions/components/TimeoutErrorDisplay'
import { isTimeoutError } from '@/features/actions/logic/useActionTimeout'
import { isUserRejection } from '@/lib/errors'
import { useEffect, useRef, useState } from 'react'
import { useDebounce } from '@/utils/useDebounce'
import { normalizeDecimalInput } from '@/utils/decimalInput'
import { BigBlocksBanner } from '@/ui/molecules/big-blocks-banner/BigBlocksBanner'
import { BorrowCapDetail } from '@/ui/molecules/borrow-cap-detail/BorrowCapDetail'
import { MAX_LTV_EPSILON } from '../logic/borrowMoreConstants'

interface EBorrowMoreViewProps {
  liabilityVault: `0x${string}`
  liabilityDecimals: number
  liabilitySymbol: string
  liabilityLogoURI?: string
  additionalBorrowAmount: string
  setAdditionalBorrowAmount: (amount: string) => void
  currentDebtFormatted: number
  ltv: number
  setLtv: (ltv: number) => void
  currentLTV: number
  safeMaxLTV: number
  collateralValueUSD: number
  liabilityPriceUSD: number
  liabilityToken: Token
  canBorrow: boolean
  impliedLtv: number
  executeStatus: ActionHandlerState['status']
  isSimulating: boolean
  isTimedOut: boolean
  simulationError: Error | null
  displayError: Error | null
  onExecute: () => void
  isRetryDisabled: boolean
}

export function EBorrowMoreView({
  liabilityVault,
  liabilityDecimals,
  liabilitySymbol,
  liabilityLogoURI,
  additionalBorrowAmount,
  setAdditionalBorrowAmount,
  currentDebtFormatted,
  ltv,
  setLtv,
  currentLTV,
  safeMaxLTV,
  collateralValueUSD,
  liabilityPriceUSD,
  liabilityToken,
  canBorrow,
  impliedLtv,
  executeStatus,
  isSimulating,
  isTimedOut,
  simulationError,
  displayError,
  onExecute,
  isRetryDisabled,
}: EBorrowMoreViewProps) {
  const tokenIconInfo = liabilityToken
    ? { symbol: liabilityToken.symbol, logoURI: liabilityLogoURI }
    : { symbol: TokenSymbol(liabilitySymbol || 'unknown'), logoURI: liabilityLogoURI }

  const [borrowInputValue, setBorrowInputValue] = useState(additionalBorrowAmount)
  const [isBorrowInputFocused, setIsBorrowInputFocused] = useState(false)
  const [ltvInputValue, setLtvInputValue] = useState(ltv.toFixed(2))
  const [isLtvInputFocused, setIsLtvInputFocused] = useState(false)
  const [sliderLtv, setSliderLtv] = useState(ltv)
  const [isSliderDragging, setIsSliderDragging] = useState(false)
  const pendingLtvRef = useRef<number | null>(null)

  const { isDebouncing: isSliderDebouncing } = useDebounce(isSliderDragging, String(isSliderDragging), { delay: 300 })
  const isSliderActive = isSliderDragging || isSliderDebouncing

  useEffect(() => {
    if (!isBorrowInputFocused) {
      setBorrowInputValue(additionalBorrowAmount)
    }
  }, [additionalBorrowAmount, isBorrowInputFocused])

  useEffect(() => {
    if (!isBorrowInputFocused && isSliderActive && currentLTV > 0) {
      const additionalBorrow = Math.max(0, (currentDebtFormatted * sliderLtv) / currentLTV - currentDebtFormatted)
      setBorrowInputValue(additionalBorrow > 0 ? additionalBorrow.toFixed(6) : '0')
    }
  }, [sliderLtv, currentDebtFormatted, currentLTV, isBorrowInputFocused, isSliderActive])

  useEffect(() => {
    setSliderLtv(ltv)
    if (!isLtvInputFocused) {
      setLtvInputValue(ltv.toFixed(2))
    }
  }, [ltv, isLtvInputFocused])

  useEffect(() => {
    if (!isSliderActive && pendingLtvRef.current !== null) {
      setLtv(pendingLtvRef.current)
      pendingLtvRef.current = null
    }
  }, [isSliderActive, setLtv])

  const displayAdditionalBorrow =
    currentLTV > 0
      ? Math.max(0, (currentDebtFormatted * sliderLtv) / currentLTV - currentDebtFormatted)
      : Number(additionalBorrowAmount)
  const displayTotalDebt = currentDebtFormatted + displayAdditionalBorrow
  const displayAdditionalBorrowUSD = displayAdditionalBorrow * liabilityPriceUSD

  const isBelowCurrent = sliderLtv < currentLTV
  const isAboveMaxLtv = impliedLtv > safeMaxLTV + MAX_LTV_EPSILON

  const actionsEnabled = canBorrow && !isBelowCurrent && !isSliderActive && !isAboveMaxLtv

  return (
    <MultiPanelDialog>
      <DialogTitle className="font-semibold text-white text-xl">Borrow More</DialogTitle>

      <DialogPanel>
        <div className="flex flex-col gap-4">
          {/* Current Position Info */}
          <div className="flex flex-col gap-2 rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground text-xs">Current Debt</span>
              <span className="text-sm text-white">
                {currentDebtFormatted.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 6,
                })}{' '}
                {liabilitySymbol}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground text-xs">Current LTV</span>
              <span className="text-sm text-white">{currentLTV.toFixed(2)}%</span>
            </div>
          </div>

          {/* Borrow Amount Input */}
          <div className="flex flex-col gap-2">
            <label className="text-muted-foreground text-xs">Amount to borrow</label>
            <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] p-3">
              <TokenIcon token={tokenIconInfo} className="h-8 w-8 shrink-0" />
              <input
                type="text"
                inputMode="decimal"
                value={borrowInputValue}
                onChange={(e) => {
                  const normalized = normalizeDecimalInput(e.target.value)
                  if (normalized === null) return
                  setBorrowInputValue(normalized)
                  setAdditionalBorrowAmount(normalized || '0')
                }}
                onFocus={() => setIsBorrowInputFocused(true)}
                onBlur={() => {
                  setIsBorrowInputFocused(false)
                  const amount = Number(borrowInputValue) || 0
                  if (collateralValueUSD > 0 && liabilityPriceUSD > 0) {
                    const maxAdditionalDebt =
                      (collateralValueUSD * safeMaxLTV) / 100 / liabilityPriceUSD - currentDebtFormatted
                    const capped = Math.min(amount, Math.max(0, maxAdditionalDebt))
                    const precision = Math.min(liabilityToken.decimals, 6)
                    const factor = 10 ** precision
                    const cappedStr = (Math.floor(capped * factor) / factor).toFixed(precision)
                    setBorrowInputValue(cappedStr)
                    setAdditionalBorrowAmount(cappedStr)
                  } else {
                    setBorrowInputValue(additionalBorrowAmount)
                  }
                }}
                className="flex-1 bg-transparent text-lg text-white outline-none placeholder:text-white/30"
                placeholder="0.00"
              />
              <span className="text-muted-foreground">{liabilitySymbol}</span>
            </div>
            {displayAdditionalBorrow > 0 && (
              <div className="text-right text-muted-foreground text-xs">
                $
                {displayAdditionalBorrowUSD.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </div>
            )}
          </div>
          {/* LTV Slider */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <label className="text-muted-foreground text-xs">Loan-to-Value (LTV)</label>
              <div className="flex items-center gap-1">
                <input
                  type="text"
                  inputMode="decimal"
                  value={ltvInputValue}
                  onChange={(e) => {
                    const normalized = normalizeDecimalInput(e.target.value)
                    if (normalized === null) return
                    setLtvInputValue(normalized)
                    const numValue = Math.min(Math.max(currentLTV, Number(normalized) || 0), safeMaxLTV)
                    pendingLtvRef.current = null
                    setLtv(numValue)
                  }}
                  onFocus={() => setIsLtvInputFocused(true)}
                  onBlur={() => {
                    setIsLtvInputFocused(false)
                    const numValue = Math.min(Math.max(currentLTV, Number(ltvInputValue) || 0), safeMaxLTV)
                    setLtvInputValue(numValue.toFixed(2))
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.currentTarget.blur()
                    }
                  }}
                  className="w-16 rounded border border-white/10 bg-white/[0.04] px-2 py-0.5 text-right text-white text-xs outline-none focus:border-primary"
                />
                <span className="text-muted-foreground text-xs">%</span>
              </div>
            </div>
            <div className="relative">
              <Slider
                value={[sliderLtv]}
                max={safeMaxLTV}
                min={0}
                step={0.1}
                onValueChange={(values) => {
                  setIsSliderDragging(true)
                  const nextValue = Math.min(safeMaxLTV, Math.max(currentLTV, values[0] ?? currentLTV))
                  setSliderLtv(nextValue)
                  pendingLtvRef.current = nextValue
                  if (!isLtvInputFocused) {
                    setLtvInputValue(nextValue.toFixed(2))
                  }
                }}
                onValueCommit={(values) => {
                  const nextValue = Math.min(safeMaxLTV, Math.max(currentLTV, values[0] ?? currentLTV))
                  pendingLtvRef.current = nextValue
                }}
                onPointerDown={() => setIsSliderDragging(true)}
                onPointerUp={() => setIsSliderDragging(false)}
                onLostPointerCapture={() => setIsSliderDragging(false)}
                onKeyUp={() => setIsSliderDragging(false)}
                onBlur={() => setIsSliderDragging(false)}
              />
              {currentLTV > 0 && (
                <div
                  className="-translate-y-1/2 pointer-events-none absolute top-1/2 h-4 w-0.5 bg-amber-500"
                  style={{ left: `${(currentLTV / safeMaxLTV) * 100}%` }}
                  title={`Minimum: ${currentLTV.toFixed(2)}%`}
                />
              )}
            </div>
            <div className="flex items-center justify-between text-white/30 text-xs">
              <span>0%</span>
              <div className="flex items-center gap-4">
                <span className="text-amber-500/70">Min {currentLTV.toFixed(2)}%</span>
                <span className="text-red-400/70">Max {safeMaxLTV.toFixed(2)}%</span>
              </div>
            </div>
            {isAboveMaxLtv && (
              <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-3 text-red-400 text-sm">
                Amount exceeds max LTV of {safeMaxLTV.toFixed(2)}%
              </div>
            )}
          </div>

          {/* Summary */}
          {displayAdditionalBorrow > 0 && (
            <div className="mt-4 flex flex-col gap-2">
              <TransactionOverviewDetailsItem label="Amount to borrow">
                +{displayAdditionalBorrow.toLocaleString(undefined, { maximumFractionDigits: 6 })} {liabilitySymbol}
              </TransactionOverviewDetailsItem>
              <TransactionOverviewDetailsItem label="New total debt">
                {displayTotalDebt.toLocaleString(undefined, { maximumFractionDigits: 6 })} {liabilitySymbol}
              </TransactionOverviewDetailsItem>
              <TransactionOverviewDetailsItem label="New LTV">{sliderLtv.toFixed(2)}%</TransactionOverviewDetailsItem>
              <BorrowCapDetail vaultAddress={liabilityVault} decimals={liabilityDecimals} symbol={liabilitySymbol} />
            </div>
          )}

          {/* Simulation Status */}
          {isSimulating && (
            <div className="mt-4 flex items-center gap-2 text-muted-foreground text-sm">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              Simulating transaction...
            </div>
          )}

          {/* Timeout error display */}
          <TimeoutErrorDisplay isTimedOut={isTimedOut} onRetry={onExecute} isRetryDisabled={isRetryDisabled} />

          {/* Error Display */}
          {displayError && !isTimeoutError(displayError) && (
            <TransactionErrorPanel
              error={displayError}
              showSimulationWarning={!!simulationError && !isUserRejection(simulationError)}
            />
          )}

          <BigBlocksBanner className="mt-4" />

          {/* Actions Grid */}
          <div className="mt-6 grid grid-cols-[auto_auto_1fr_auto] gap-x-4">
            <ActionRow index={1}>
              <ActionRow.Icon path={assets.actions.borrow} actionStatus={actionsEnabled ? executeStatus : 'disabled'} />
              <ActionRow.Title actionStatus={actionsEnabled ? executeStatus : 'disabled'}>
                Borrow {liabilitySymbol}
              </ActionRow.Title>
              <ActionRow.Description
                successMessage="Borrowed!"
                actionStatus={actionsEnabled ? executeStatus : 'disabled'}
                variant="compact"
              />
              <ActionRow.Action onAction={onExecute} status={actionsEnabled ? executeStatus : 'disabled'}>
                {isTimedOut ? 'Retry' : isSimulating ? 'Preparing...' : 'Borrow'}
              </ActionRow.Action>
            </ActionRow>
          </div>
        </div>
      </DialogPanel>
    </MultiPanelDialog>
  )
}
